import React from 'react';
import { BookOpen, Layers, Leaf, PackageCheck, type LucideIcon } from 'lucide-react';
import { Reveal } from './Reveal';
import { Section } from './Section';
import styles from './ProductSection.module.css';

const kitItems: Array<{
  number: string;
  name: string;
  ritual: string;
  desc: string;
  Icon: LucideIcon;
}> = [
  {
    number: '01',
    name: 'The Seeds',
    ritual: 'Where every ritual begins',
    desc: 'Three premium organic microgreen varieties — chosen for flavor, speed of growth, and the satisfaction of watching something alive unfold in your hands.',
    Icon: Leaf,
  },
  {
    number: '02',
    name: 'Pure Growing Medium',
    ritual: 'The cleanest foundation',
    desc: 'Natural coconut fiber substrate. Zero chemicals. Zero shortcuts. The simplest, most honest start possible for food you\'ll actually eat.',
    Icon: Layers,
  },
  {
    number: '03',
    name: 'Your Ritual Tray',
    ritual: 'Your daily anchor',
    desc: 'A minimal tray designed to live on your windowsill — not hide in a cabinet. It becomes part of your space. Part of your mornings.',
    Icon: PackageCheck,
  },
  {
    number: '04',
    name: 'The Ritual Guide',
    ritual: 'Seven days of practice',
    desc: 'Not an instruction manual. A daily practice — morning by morning, step by step. Designed to make the ritual feel effortless and worth repeating.',
    Icon: BookOpen,
  },
];

interface ProductSectionProps {
  onPrimaryCtaClick: () => void;
}

export function ProductSection({ onPrimaryCtaClick }: ProductSectionProps) {
  return (
    <Section id="produkt" title="Inside Your Ritual Kit">
      <Reveal>
        <p className={styles.intro}>
          Everything you need. Nothing you don't. Designed to fit your life — not demand more of it.
        </p>
      </Reveal>


      <div className={styles.layout}>
        <div className={styles.itemsGrid}>
          {kitItems.map((item, index) => (
            <Reveal key={item.number} delayMs={index * 90}>
              <article className={styles.kitItem}>
                <div className={styles.kitItemTop}>
                  <span className={styles.kitNumber}>{item.number}</span>
                  <div className={styles.kitIconWrap} aria-hidden="true">
                    <item.Icon className={styles.kitIcon} strokeWidth={1.5} />
                  </div>
                </div>
                <h3 className={styles.kitName}>{item.name}</h3>
                <p className={styles.kitRitual}>{item.ritual}</p>
                <p className={styles.kitDesc}>{item.desc}</p>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delayMs={150} className={styles.ctaColumn}>
          <div className={styles.priceCard}>
            <div className={styles.priceCardTop}>
              <p className={styles.priceLabel}>Your ritual starts at</p>
              <p className={styles.price}>$49</p>
              <p className={styles.priceSub}>Free delivery · NYC · First batch only</p>
            </div>

            <div className={styles.divider} />

            <div className={styles.philosophyBlock}>
              <p className={styles.philosophyQuote}>
                "Five minutes a day. Something growing. Something real."
              </p>
              <p className={styles.philosophyText}>
                In a city that never slows down, this is your quiet corner.
                Your windowsill. Your harvest. Your ritual.
              </p>
            </div>

            <button type="button" className={styles.ctaButton} onClick={onPrimaryCtaClick}>
              Reserve My Kit — $49
            </button>
            <p className={styles.microtext}>No payment now. Just your spot in the first batch.</p>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

export default ProductSection;
